import React, { useEffect, useState } from 'react';
import { fetchWithRetry } from '../utils/tourUtils';
import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from './ui/use-toast';

const SpaceTypeSelector = ({ value, onChange, excludeValues = [] }) => {
  const [allOptions, setAllOptions] = useState([]);
  const [newType, setNewType] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchOptions = async () => {
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.findSpaceTypes',
          payload: {},
        },
        label: 'fetch space types',
      });
      const types = result.map(t => t.type);
      setAllOptions(types);
    } catch (err) {
      console.error('Failed to fetch space types:', err);
      toast({ title: 'Error', description: 'Cannot load space types', variant: 'destructive' });
    }
  };

  const filteredOptions = allOptions.filter(
    t =>
      (!excludeValues.includes(t) || t === value) &&
      t.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const deleteType = async type => {
    if (!window.confirm(`Are you sure you want to delete "${type}"?`)) return;
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.deleteSpaceType',
          payload: { data: { type } },
        },
        label: 'delete space type',
      });
      toast({ title: 'Deleted', description: 'Space type deleted' });
      fetchOptions();
    } catch (err) {
      console.error('Error deleting space type:', err);
      toast({ title: 'Error', description: 'Failed to delete space type', variant: 'destructive' });
    }
  };

  const addType = async () => {
    if (!newType.trim()) return;
    setLoading(true);
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.createSpaceType',
          payload: { type: newType },
        },
        label: 'create space type',
      });
      toast({ title: 'Added', description: 'Space type added' });
      setNewType('');
      fetchOptions();
    } catch (err) {
      console.error('Error creating space type:', err);
      toast({ title: 'Error', description: 'Failed to add space type', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  return (
    <div className="space-y-2">
      {/* Search Input or Toggle Button */}
      {searchOpen ? (
        <div className="flex items-center gap-2 m-2">
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="חפש סוג חדר"
          />
          <Button
            variant="outline"
            onClick={() => {
              setSearchOpen(false);
              setSearchTerm('');
            }}
          >
            ❌
          </Button>
        </div>
      ) : (
        <Button variant="outline" className="m-2" onClick={() => setSearchOpen(true)}>
          ➕ הוסף חיפוש
        </Button>
      )}

      {/* Manual Search List */}
      {searchOpen && (
        <div className="bg-white rounded shadow p-2 max-h-48 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <div
                key={idx}
                className="flex justify-between items-center p-1 border-b last:border-none hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  onChange(option);
                  setSearchOpen(false);
                  setSearchTerm('');
                }}
              >
                <div>{option}</div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-red-500"
                  onClick={e => {
                    e.stopPropagation();
                    deleteType(option);
                  }}
                >
                  🗑️
                </Button>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 p-2">No matching types</div>
          )}
        </div>
      )}

      {/* Normal Select Dropdown */}
      <Select
        value={value}
        onValueChange={onChange}
        onOpenChange={open => {
          if (!open) setSearchTerm('');
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="בחר סוג חדר" />
        </SelectTrigger>
        <SelectContent>
          {filteredOptions.map((option, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <SelectItem value={option}>{option}</SelectItem>
              <Button
                size="icon"
                variant="ghost"
                className="text-red-500"
                onClick={e => {
                  e.stopPropagation();
                  deleteType(option);
                }}
              >
                🗑️
              </Button>
            </div>
          ))}
        </SelectContent>
      </Select>

      {/* Add New Type */}
      <div className="flex gap-2 flex-col sm:flex-row">
        <Input
          value={newType}
          onChange={e => setNewType(e.target.value)}
          placeholder="הוסף סוג חדר חדש"
        />
        <Button onClick={addType} disabled={loading || !newType}>
          הוסף
        </Button>
      </div>
    </div>
  );
};

export default SpaceTypeSelector;
